// Single source of truth for the partner co-branding feature identifiers.
// Keeping these here prevents the flag key and feature key from drifting across
// the sidebar gate, the branding builder, the auth-screen logo, and the admin
// product/feature catalogues.

/** Master feature-flag key that switches partner branding on globally. */
export const PARTNER_BRANDING_FLAG = 'partner-branding-enabled';

/**
 * Canonical feature value stored in an organisation's grant `features[]`.
 * Compared case-insensitively at every gate (see `hasPartnerBrandingFeature`)
 * because the value can be authored in mixed casing.
 */
export const PARTNER_BRANDING_FEATURE = 'partnerBranding';

/** Lowercased form used for case-insensitive comparisons. */
export const PARTNER_BRANDING_FEATURE_LOWER = PARTNER_BRANDING_FEATURE.toLowerCase();

/**
 * True when the given grant feature list includes the partner-branding
 * entitlement, matched case-insensitively.
 */
export const hasPartnerBrandingFeature = (features?: readonly string[] | null): boolean =>
	Array.isArray(features) &&
	features.some((feature) => String(feature).toLowerCase() === PARTNER_BRANDING_FEATURE_LOWER);

// -------------------- "Learn more" button (PTEC-1048) --------------------
// An optional call-to-action an organisation can add to its co-branded sign-in
// and create-account screens. Label, link, style and colours are all theirs; the
// limits below mirror the organisations Lambda's validation.

export const LEARN_MORE_STYLES = ['contained', 'outlined', 'text'] as const;
export type LearnMoreStyle = (typeof LEARN_MORE_STYLES)[number];

export const LEARN_MORE_LABEL_MAX = 40;
export const LEARN_MORE_URL_MAX = 2048;

/** What the public branding endpoint returns when the button is switched on. */
export interface LearnMoreButtonConfig {
	label: string;
	url: string;
	style: LearnMoreStyle;
	/** Background for `contained`; text and outline for the others. Unset uses the theme. */
	colour?: string | null;
	/** Text colour for `contained`. Unset uses the theme's contrast text. */
	textColour?: string | null;
}

/** What the organisation stores: the button plus whether it is shown. */
export interface LearnMoreSettings extends Omit<LearnMoreButtonConfig, 'label' | 'url'> {
	enabled: boolean;
	label?: string | null;
	url?: string | null;
}

/** The only links rendered on the auth screens: absolute https addresses. */
export const isHttpsUrl = (value: string): boolean => {
	try {
		return new URL(value).protocol === 'https:';
	} catch {
		return false;
	}
};

export type LearnMoreErrorCode = 'labelRequired' | 'labelLength' | 'urlRequired' | 'urlInvalid' | 'colourInvalid';

export interface LearnMoreErrors {
	label?: LearnMoreErrorCode;
	url?: LearnMoreErrorCode;
	colour?: LearnMoreErrorCode;
	textColour?: LearnMoreErrorCode;
}

const HEX_COLOUR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * Field errors for the builder. A disabled button may be incomplete so the
 * organisation can keep a draft; an enabled one needs a label and link.
 */
export const validateLearnMore = (settings: LearnMoreSettings): LearnMoreErrors => {
	const errors: LearnMoreErrors = {};
	const label = settings.label?.trim() ?? '';
	const url = settings.url?.trim() ?? '';

	if (label.length > LEARN_MORE_LABEL_MAX) {
		errors.label = 'labelLength';
	} else if (settings.enabled && !label) {
		errors.label = 'labelRequired';
	}

	if (url && (url.length > LEARN_MORE_URL_MAX || !isHttpsUrl(url))) {
		errors.url = 'urlInvalid';
	} else if (settings.enabled && !url) {
		errors.url = 'urlRequired';
	}

	if (settings.colour && !HEX_COLOUR_PATTERN.test(settings.colour)) {
		errors.colour = 'colourInvalid';
	}
	if (settings.textColour && !HEX_COLOUR_PATTERN.test(settings.textColour)) {
		errors.textColour = 'colourInvalid';
	}
	return errors;
};
